import { useState, useEffect, useRef, memo, useCallback } from "react";
import { BrowserRouter as Router, Link, useLocation, useNavigate } from "react-router-dom";
import TopLoadingBar, { topLoader } from './components/TopLoadingBar';
import { HelmetProvider } from "react-helmet-async";
import AppRoutes from './routes/AppRoutes';
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ChatInputProvider, useChatInput } from "./context/ChatInputContext";
import { PopupProvider, usePopup } from "./context/PopupContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Sidebar from "./components/Sidebar";
// import FocusButton from "./components/FocusButton";
import LoadingScreen from "./components/LoadingScreen";
import ChatInputFixed from './components/ChatInputFixed';
import ChatSidebar from './components/ChatSidebar';
import ChatBreadcrumb from './components/ChatBreadcrumb';
import PdfViewerPanel from './components/PdfViewerPanel';
import DiagramEditorPanel from './components/DiagramEditorPanel';
import SpreadsheetEditorPanel from './components/SpreadsheetEditorPanel';
import CircuitEditorPanel from './components/CircuitEditorPanel';
import VerifyEmail from "./auth/VerifyEmail";
import DebugToggle from './components/DebugToggle';
import visitorTracker from './utils/visitorTracking';
import memoryApi from './api/memoryApi';
import usePaginatedCache, { clearPaginatedCache } from './hooks/usePaginatedCache';

import { GoogleOAuthProvider } from "@react-oauth/google";
import CookieConsent from "react-cookie-consent";
import { VoiceProvider, useVoice } from './context/VoiceContext';
import VoiceBallOverlay from './components/VoiceBallOverlay'; 


// Custom hooks
import useResponsiveLayout from './hooks/useResponsiveLayout';
import useNavbarVisibility from './hooks/useNavbarVisibility';
import useCookieConsent from './hooks/useCookieConsent';
import useAuthStatus from './hooks/useAuthStatus';


const categories = [];
const GOOGLE_OAUTH_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;


// Initialize orientation tracking
if (typeof document !== 'undefined') {
  const isLandscape = window.innerWidth > window.innerHeight;
  document.documentElement.dataset.lastOrientation = isLandscape ? 'landscape' : 'portrait';
}



// DECOUPLED SIDEBAR WRAPPER - No context subscriptions
const SidebarWrapper = memo(({ categories, currentOrientation, isSidebarOpen, setIsSidebarOpen, messages, clearChatHistory, isMobile }) => {
  const { showPopup } = usePopup(); // Only this wrapper subscribes to context

  return (
    <Sidebar
      categories={categories}
      currentOrientation={currentOrientation}
      isOpen={isSidebarOpen}
      setIsOpen={setIsSidebarOpen}
      messages={messages}
      showPopup={showPopup}
      clearChatHistory={clearChatHistory}
      isMobile={isMobile}
    />
  );
});

// Module-scope stable reference. Used as the fallback for `messages` when
// the per-session bucket is undefined — sharing one reference across
// renders prevents the infinite loop documented inline below.
const EMPTY_MESSAGES = Object.freeze([]);

const AppContent = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const { isVoiceOverlayVisible, voiceState, isVoiceActive, handleEndVoice } = useVoice();

  const { user, loading, logout, registerLogoutCallback } = useAuth();

  // ============================================
  // UNIVERSAL CHAT SIDEBAR STATE (for all /chatty/* routes)
  // ============================================
  const [chatSidebarOpen, setChatSidebarOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  // Paginated session cache. Hook owns:
  //   - localStorage SWR cache (chatty_session_cache_<sub>)
  //   - viewport-aware visible slice
  //   - load-more (visible bump + backend prefetch when cache low)
  // user.sub keys the cache so multi-user-on-shared-device stays isolated.
  const sessionsCacheKey = user?.sub ? `chatty_session_cache_${user.sub}` : null;
  const fetchSessionsPage = useCallback(
    ({ limit, cursor }) => memoryApi.listSessionsPage({ limit, cursor }),
    []
  );
  const {
    items: chatSessions,
    setItems: setChatSessions,
    visible: visibleChatSessions,
    loading: sessionsLoading,
    loadingMore: sessionsLoadingMore,
    hasMore: sessionsHasMore,
    loadMore: loadMoreSessions,
    refresh: refreshSessionsCache,
  } = usePaginatedCache({
    cacheKey: sessionsCacheKey,
    fetchPage: fetchSessionsPage,
    enabled: !!user,
    pageSize: 50,
  });

  // Logout cleanup: clear all per-user state + redirect off any session URL
  // the moment user transitions from authed to null. Without this the
  // sidebar still shows the prior user's sessions, the URL keeps pointing
  // at an inaccessible session (401 cascade in console), and breadcrumb
  // remains stuck on the prior chat — privacy leak across users on a
  // shared device.
  const prevUserRef = useRef(null);
  useEffect(() => {
    const wasLoggedIn = !!prevUserRef.current;
    const isLoggedIn = !!user;
    if (wasLoggedIn && !isLoggedIn) {
      // Wipe the prior user's localStorage caches before nulling the
      // ref so we still know who to scope the cache key to.
      const priorSub = prevUserRef.current?.sub;
      if (priorSub) {
        clearPaginatedCache(`chatty_session_cache_${priorSub}`);
        clearPaginatedCache(`chatty_project_cache_${priorSub}`);
      }
      setChatSessions([]);
      setCurrentSessionId(null);
      setMessagesBySession({});
      setIsLoadingBySession({});
      if (location.pathname.startsWith('/chatty/')) {
        navigate('/chatty', { replace: true });
      }
    }
    prevUserRef.current = user;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync currentSessionId from URL. App.js owns `messagesBySession` —
  // derived `messages = messagesBySession[currentSessionId]` and
  // `isLoading = isLoadingBySession[currentSessionId]` only resolve to
  // the right bucket when this state tracks the URL. Programmatic
  // navigations (useChatSend's `navigate('/chatty/<id>', { replace: true })`
  // after creating a new session) change the URL without going through
  // handleSelectSession, so they relied on this sync to land in the
  // right bucket. Without it: stream writes to messagesBySession[<new_id>]
  // but the view reads messagesBySession[null] → empty + spinner stuck.
  useEffect(() => {
    // Match `/chatty/<id>` only — anchored end excludes nested paths
    // like `/chatty/projects/<id>`. Also reject the literal "projects"
    // segment (`/chatty/projects` → ProjectsPage route, not a chat
    // session). Without these guards App's currentSessionId would
    // briefly equal "projects" while on project routes.
    const m = location.pathname.match(/^\/chatty\/([^/]+)$/);
    const id = m && m[1] !== 'projects' ? m[1] : null;
    setCurrentSessionId(id);
  }, [location.pathname]);

  // Get refresh trigger + setter registration from context
  const { sessionsRefreshTrigger, registerSetChatSessions } = useChatInput();

  // Register our setChatSessions with context so useChatSend can bump
  // a session's `last_accessed` optimistically without re-fetching.
  useEffect(() => {
    registerSetChatSessions(setChatSessions);
  }, [registerSetChatSessions]);

  // The old loadSessions effect is replaced by usePaginatedCache (above):
  //   - cache hydrate on mount via localStorage (instant first paint)
  //   - SWR fresh fetch in background, replaces cache when fresh arrives
  //   - viewport-aware visible slice + load-more
  //
  // sessionsRefreshTrigger from ChatInputContext (delete/rename callers
  // bump it) maps to hook.refresh() — re-runs the initial fetch.
  useEffect(() => {
    if (sessionsRefreshTrigger > 0) {
      refreshSessionsCache();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsRefreshTrigger]);

  // Top loading bar on route change
  useEffect(() => {
    topLoader.start();
    const timer = setTimeout(() => topLoader.done(), 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Session handlers for sidebar
  const handleSelectSession = useCallback((sessionId) => {
    setCurrentSessionId(sessionId);
    navigate(`/chatty/${sessionId}`);
  }, [navigate]);

  const handleCreateSession = useCallback(() => {
    // Just navigate to empty chat - session created on first message
    setCurrentSessionId(null);
    setChatSidebarOpen(false);
    navigate('/chatty');
  }, [navigate]);

  const handleDeleteSession = useCallback(async (sessionId) => {
    try {
      await memoryApi.deleteSession(sessionId);
      setChatSessions(prev => prev.filter(s => s.session_id !== sessionId));
      // Check if deleting the currently active session (from URL, not state)
      const urlSessionId = location.pathname.match(/\/chatty\/([^/]+)/)?.[1];
      if (urlSessionId === sessionId || currentSessionId === sessionId) {
        setCurrentSessionId(null);
        navigate('/chatty');
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  }, [currentSessionId, navigate, location.pathname]);

  const handleRenameSession = useCallback(async (sessionId, title) => {
    try {
      await memoryApi.renameSession(sessionId, title);
      setChatSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, title } : s
      ));
    } catch (error) {
      console.error('Failed to rename session:', error);
    }
  }, []);

  const handleMoveToProject = useCallback(async (sessionId, projectId) => {
    try {
      await memoryApi.updateSessionProject(sessionId, projectId);
      setChatSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, project_id: projectId } : s
      ));
    } catch (error) {
      console.error('Failed to move session:', error);
    }
  }, []);

  // Track page views
  useEffect(() => {
    visitorTracker.trackPageView(location.pathname);
  }, [location.pathname]);

  // ============================================
  // SIDEBAR VISIBILITY CONFIGURATION
  // ============================================
  // Set to true to show sidebar (both expanded and collapsed states)
  // Set to false to completely hide sidebar (full-width layout)
  // ============================================
  // eslint-disable-next-line no-unused-vars
  const [sidebarVisible, setSidebarVisible] = useState(false); // Change to true to show sidebar

  const {
    isSidebarOpen, setIsSidebarOpen,
    isFocusMode,
    isMobile, isTablet, isMobileOrTablet, currentOrientation,
    // focusModeLeftSpace, setFocusModeLeftSpace,
    // normalModeLeftSpace, setNormalModeLeftSpace,
    layoutClasses, layoutKey,
    handleToggleSidebar, // handleToggleFocusMode
  } = useResponsiveLayout(location.pathname, sidebarVisible);

  // Then add this one which depends on values from the previous hook
  const hideNavbar = useNavbarVisibility(isMobile, currentOrientation, isFocusMode, isMobileOrTablet);
// Add the hook at the top of your AppContent component
const {
  showVerifyPopup, setShowVerifyPopup,
  registeredEmail, setRegisteredEmail,
  isRedirecting, isOAuthRedirect
} = useAuthStatus({ user, loading, logout });

  
  
  useEffect(() => {
    // Only apply this on desktop
    if (window.innerWidth >= 768) {
      const container = document.querySelector('.content-container');
      if (!container) return;
      
      const checkAndTriggerReflow = () => {
        if (container.scrollHeight > container.clientHeight) {
  
          void container.offsetHeight; // Trigger reflow

        }
      };
      
      setTimeout(checkAndTriggerReflow, 300);
      
      const resizeObserver = new ResizeObserver((entries) => {
        // Use requestAnimationFrame to avoid ResizeObserver loop error
        window.requestAnimationFrame(() => {
          if (entries && entries.length) {
            checkAndTriggerReflow();
          }
        });
      });
      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }
  }, [location.pathname]);
  

  // Per-session message + isLoading state. Streams from session A keep
  // writing to messagesBySession[A] / isLoadingBySession[A] while the
  // user is viewing/chatting in session B — no cross-session bleed.
  // The active-view derived getters (messages, isLoading) read from
  // currentSessionId's bucket; switching is just a re-key, no reset.
  const [messagesBySession, setMessagesBySession] = useState({});
  const [isLoadingBySession, setIsLoadingBySession] = useState({});
  // Stable empty-array reference for buckets that haven't been populated
  // yet. Without this, every render produces a NEW [] when the bucket is
  // undefined → handleSendMessage's useCallback (which has `messages` in
  // deps) recreates → chatInputPropsObj re-memos → setChatInputProps
  // fires from Chatty.js's useEffect → context update → re-render → loop.
  // "Maximum update depth exceeded" was reproducing on /chatty mount.
  const messages = currentSessionId ? (messagesBySession[currentSessionId] || EMPTY_MESSAGES) : EMPTY_MESSAGES;
  const isLoading = currentSessionId ? !!isLoadingBySession[currentSessionId] : false;

  // Session-bound writers — capture session_id at call time so streams
  // continue writing to their original session even after navigation.
  const setMessagesForSession = useCallback((sessionId, updater) => {
    if (!sessionId) return;
    setMessagesBySession(prev => {
      const sessionMsgs = prev[sessionId] || [];
      const newMsgs = typeof updater === 'function' ? updater(sessionMsgs) : updater;
      return { ...prev, [sessionId]: newMsgs };
    });
  }, []);
  const setIsLoadingForSession = useCallback((sessionId, value) => {
    if (!sessionId) return;
    setIsLoadingBySession(prev => {
      const newVal = typeof value === 'function' ? value(!!prev[sessionId]) : value;
      // Drop the key entirely when going to false to keep the map small.
      if (!newVal) {
        const { [sessionId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [sessionId]: true };
    });
  }, []);

  // Active-session shortcut writers — preserve the existing call sites
  // (Chatty etc.) that just want "the current session's stuff."
  const setMessages = useCallback((updater) => {
    setMessagesForSession(currentSessionId, updater);
  }, [currentSessionId, setMessagesForSession]);
  const setIsLoading = useCallback((value) => {
    setIsLoadingForSession(currentSessionId, value);
  }, [currentSessionId, setIsLoadingForSession]);

  // Register cleanup callback for logout
  useEffect(() => {
    if (registerLogoutCallback) {
      const unregister = registerLogoutCallback(() => {
        setMessagesBySession({});
        setIsLoadingBySession({});
        localStorage.removeItem("chattyMessages");
        localStorage.removeItem("chattyPersonality");
      });

      return unregister;
    }
  }, [registerLogoutCallback]);

  const {
    cookiePreferences, setCookiePreferences,
    handleAcceptConsent, handleDeclineConsent,
    shouldShowBanner
  } = useCookieConsent(location.pathname, isOAuthRedirect);
  // Chat input props from Chatty via context
  const { chatInputProps } = useChatInput();
  
// Log hideNavbar changes
useEffect(() => {
  // console.log('[App.js] hideNavbar Updated:', hideNavbar);
}, [hideNavbar]);

  // Session-switch reset removed — per-session state means switching is
  // just a re-key on currentSessionId. messagesBySession[A] stays intact
  // while user is on B, so A's in-flight stream (long tool call etc.)
  // keeps writing to its own bucket and is visible when user navigates
  // back. Coming back from non-chatty pages re-renders from the bucket.

  return (
    <div className={`flex flex-col min-h-screen chatty-debug-container ${process.env.NODE_ENV === 'development' ? 'debug-enabled' : ''}`}>

      {/* Top Loading Bar */}
      <TopLoadingBar />

      {/* Focus Mode Button */}
      {/* {!showVerifyPopup && location.pathname !== "/chatty" && (
        <div className="fixed top-4 right-6 z-[60]">
          <FocusButton 
            toggleFocusMode={handleToggleFocusMode} 
            isFocusMode={isFocusMode}
            showSlider={location.pathname === "/editor"}
            focusModeLeftSpace={focusModeLeftSpace}
            setFocusModeLeftSpace={setFocusModeLeftSpace}
            normalModeLeftSpace={normalModeLeftSpace}
            setNormalModeLeftSpace={setNormalModeLeftSpace}
          />
        </div>
      )} */}
      {/* Render ChatInputFixed on Chatty chat pages (not project pages) */}
      {(location.pathname === "/chatty" || (location.pathname.startsWith("/chatty/") && !location.pathname.includes("/projects"))) && chatInputProps && <ChatInputFixed sidebarOpen={chatSidebarOpen} />}

      {/* Render ChatSidebar and hamburger at App level for fixed positioning */}
      {location.pathname.startsWith("/chatty") && (
        <>
          <ChatSidebar
            isOpen={chatSidebarOpen}
            sessions={visibleChatSessions}
            currentSessionId={currentSessionId}
            onSelectSession={handleSelectSession}
            onCreateSession={handleCreateSession}
            onDeleteSession={handleDeleteSession}
            onRenameSession={handleRenameSession}
            onMoveToProject={handleMoveToProject}
            isLoading={sessionsLoading}
            hasMore={sessionsHasMore}
            loadingMore={sessionsLoadingMore}
            onLoadMore={loadMoreSessions}
          />
          {/* Hamburger button - styles in CSS, only dynamic left here */}
          <button
            onClick={() => setChatSidebarOpen(prev => !prev)}
            className="chat-sessions-toggle"
            style={{ left: chatSidebarOpen ? '290px' : '10px' }}
            aria-label={chatSidebarOpen ? 'Close chat sessions' : 'Open chat sessions'}
          >
            {chatSidebarOpen ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 448 512" style={{ color: 'var(--text-primary)' }}>
                <path d="M160 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 64-64 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l96 0c17.7 0 32-14.3 32-32l0-96zm0 384l0-96c0-17.7-14.3-32-32-32l-96 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l64 0 0 64c0 17.7 14.3 32 32 32s32-14.3 32-32zM288 64c0 17.7 14.3 32 32 32l64 0 0 64c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96c0-17.7-14.3-32-32-32l-96 0c-17.7 0-32 14.3-32 32zM448 352c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 64-64 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l96 0c17.7 0 32-14.3 32-32l0-96z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 448 512" style={{ color: 'var(--text-primary)' }}>
                <path d="M32 32C14.3 32 0 46.3 0 64l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-64 64 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L32 32zM64 352c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7 14.3 32 32 32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-64 0 0-64zM320 32c-17.7 0-32 14.3-32 32s14.3 32 32 32l64 0 0 64c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96c0-17.7-14.3-32-32-32l-96 0zM448 352c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 64-64 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l96 0c17.7 0 32-14.3 32-32l0-96z"/>
              </svg>
            )}
          </button>
          {/* New chat button - right side, mobile portrait */}
          <button
            onClick={handleCreateSession}
            className="chat-new-session-btn"
            aria-label="New chat"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          {/* Breadcrumb for project/session navigation */}
          <ChatBreadcrumb
            sidebarOpen={chatSidebarOpen}
            sessions={chatSessions}
            onRenameSession={handleRenameSession}
            onDeleteSession={handleDeleteSession}
          />
          {/* PDF Viewer Panel - fixed right side, floats like sidebar */}
          <PdfViewerPanel />
          {/* Diagram Editor Panel - Draw.io editor, same pattern */}
          <DiagramEditorPanel />
          {/* Spreadsheet Editor Panel - Fortune Sheet, same pattern */}
          <SpreadsheetEditorPanel />
          {/* Circuit Editor Panel - tscircuit design viewer */}
          <CircuitEditorPanel />
        </>
      )}      

      
      {/* Loading Screen */}
      {(loading || isRedirecting) && <LoadingScreen />}
      
      {/* Email Verification Popup */}
      {showVerifyPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex justify-center items-center">
          <VerifyEmail email={registeredEmail} setShowVerifyPopup={setShowVerifyPopup} />
        </div>
      )}
      
      {/* Debug Toggle with Memory Profiler - development only */}
      {process.env.NODE_ENV === 'development' && <DebugToggle />}
      


<div 
  className={`fixed left-0 w-full z-[70]`}
  style={{
    pointerEvents: (isFocusMode || hideNavbar) ? 'none' : 'auto'
  }}
>
<Navbar
  isMobile={isMobile}
  isTablet={isTablet}
  isMobileOrTablet={isMobileOrTablet}
  setIsSidebarOpen={setIsSidebarOpen}
  isSidebarOpen={isSidebarOpen}
  toggleSidebar={handleToggleSidebar}
  currentOrientation={currentOrientation}
  isFocusMode={isFocusMode}
  hideNavbar={hideNavbar}
  messages={messages}
  clearChatHistory={async () => {
    try {
      // 1. Clear localStorage immediately (instant UX feedback)
      localStorage.removeItem("chattyMessages");
      localStorage.removeItem("chattyPersonality");
      setMessages([]);

      // 2. Clear cloud storage (DynamoDB) in background
      const memoryApi = (await import('./api/memoryApi.js')).default;
      await memoryApi.clearMessages();

      console.log('Chat cleared');
    } catch (error) {
      console.error('Failed to clear cloud storage:', error);
      // Local storage still cleared - user gets immediate feedback
      // Cloud will be cleared on next sync
    }
  }}
/>
</div>




{sidebarVisible && (
<div
  className={`fixed top-0 left-0 h-full z-[60] ${isFocusMode ? "opacity-0 pointer-events-none" : ""}`}
  style={{
    width: isSidebarOpen ? '250px' : '50px',
    transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden'
  }}
>
  <SidebarWrapper
    categories={categories}
    currentOrientation={currentOrientation}
    isSidebarOpen={isSidebarOpen}
    setIsSidebarOpen={setIsSidebarOpen}
    messages={messages}
    isMobile={isMobile}
    clearChatHistory={async () => {
      try {
        // 1. Clear localStorage immediately (instant UX feedback)
        localStorage.removeItem("chattyMessages");
        localStorage.removeItem("chattyPersonality");
        setMessages([]);
        
        // 2. Clear cloud storage (DynamoDB) in background
        const memoryApi = (await import('./api/memoryApi.js')).default;
        await memoryApi.clearMessages();
        
        console.log('Chat cleared');
      } catch (error) {
        console.error('Failed to clear cloud storage:', error);
        // Local storage still cleared - user gets immediate feedback
        // Cloud will be cleared on next sync
      }
    }}
  />
</div>
)}

      {/* Main Content Container */}
      <div className={`${layoutClasses.contentContainer} chatty-debug-content`}>
        {/* Sidebar */}

        
        {/* Main Content Area */}
        <main
          className={`${layoutClasses.mainContent} chatty-debug-main ${!sidebarVisible ? 'sidebar-hidden' : ''}`}
          key={`${layoutKey}-${currentOrientation}`}
          id="primary-main-content"
        >
          <AppRoutes
            messages={messages}
            setMessages={setMessages}
            setMessagesForSession={setMessagesForSession}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setIsLoadingForSession={setIsLoadingForSession}
            isFocusMode={isFocusMode}
            showVerifyPopup={showVerifyPopup}
            setShowVerifyPopup={setShowVerifyPopup}
            registeredEmail={registeredEmail}
            setRegisteredEmail={setRegisteredEmail}
            isMobile={isMobile}
            currentOrientation={currentOrientation}
            chatSidebarOpen={chatSidebarOpen}
            setChatSidebarOpen={setChatSidebarOpen}
          />

          {/* Footer - hide on mobile chatty when input is at bottom */}
          <Footer
            isFocusMode={isFocusMode}
            hideOnMobileChat={
              location.pathname.startsWith("/chatty") &&
              !location.pathname.includes("/projects") &&
              chatInputProps &&
              !chatInputProps.isCentered
            }
          />
        </main>

      </div>
      
 {/* Cookie Consent Banner */}
{shouldShowBanner && (
  <CookieConsent
    location="bottom"
    buttonText="Accept Selected"
    declineButtonText="Decline All"
    cookieName="userConsent"
    style={{ background: '#2B373B', color: '#fff', padding: '15px' }}
    buttonStyle={{ background: '#4e503b', color: 'white', padding: '8px 15px', marginLeft: '10px' }}
    declineButtonStyle={{ background: '#c0c0c0', color: 'black', padding: '8px 15px', marginLeft: '10px' }}
    acceptOnScroll={false}
    acceptOnScrollPercentage={0}
    expires={150}
    enableDeclineButton
    setDeclineCookie={true}
    contentStyle={{ flex: '1 0 300px', margin: '15px' }}
    containerClasses="cookie-consent-container"
    buttonWrapperClasses="cookie-consent-buttons"
    overlay={true}
    overlayStyle={{ background: 'rgba(0,0,0,0.5)' }}
    flipButtons={false}
    onAccept={handleAcceptConsent}
    onDecline={handleDeclineConsent}
  >
    <div>
      <h4 style={{ margin: '0 0 10px 0' }}>Cookie Preferences</h4>
      <p>This website uses cookies to enhance your experience. Please select which categories of cookies you consent to:</p>
      <div style={{ margin: '10px 0' }}>
        <div style={{ marginBottom: '8px' }}>
          <input type="checkbox" id="necessary-cookies" checked disabled />
          <label htmlFor="necessary-cookies" style={{ marginLeft: '5px', fontWeight: 'bold' }}>Necessary Cookies (Required)</label>
          <p style={{ margin: '3px 0 0 20px', fontSize: '0.9em' }}>These cookies are essential for the website to function properly.</p>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <input 
            type="checkbox" 
            id="analytics-cookies" 
            name="analytics" 
            checked={cookiePreferences.analytics}
            onChange={(e) => setCookiePreferences({ ...cookiePreferences, analytics: e.target.checked })}
          />
          <label htmlFor="analytics-cookies" style={{ marginLeft: '5px', fontWeight: 'bold' }}>Analytics Cookies</label>
          <p style={{ margin: '3px 0 0 20px', fontSize: '0.9em' }}>Help us understand how visitors interact with our website.</p>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <input 
            type="checkbox" 
            id="preference-cookies" 
            name="preferences" 
            checked={cookiePreferences.preferences}
            onChange={(e) => setCookiePreferences({ ...cookiePreferences, preferences: e.target.checked })}
          />
          <label htmlFor="preference-cookies" style={{ marginLeft: '5px', fontWeight: 'bold' }}>Preference Cookies</label>
          <p style={{ margin: '3px 0 0 20px', fontSize: '0.9em' }}>Allow the website to remember your preferences and settings.</p>
        </div>
      </div>
      <p style={{ fontSize: '0.9em', marginTop: '10px' }}>
        For detailed information about the cookies we use, see our{' '}
        <Link to="/privacy" style={{ color: '#fff', textDecoration: 'underline' }}>Privacy Policy</Link>{' '}
        and{' '}
        <Link to="/terms" style={{ color: '#fff', textDecoration: 'underline' }}>Terms & Conditions</Link>.
      </p>
      <a 
        href="/privacy#cookies"
        style={{ color: '#fff', textDecoration: 'underline', padding: '5px', cursor: 'pointer', fontWeight: '500' }}
        onClick={(e) => {
          e.preventDefault();
          localStorage.setItem('cookieBannerTemporarilyHidden', new Date().getTime().toString());
          window.location.href = '/privacy#cookies';
        }}
      >
        Cookie Details
      </a>
    </div>
  </CookieConsent>
)}

      {/* Voice Ball Overlay - positioned above everything */}
      <VoiceBallOverlay
        isVisible={isVoiceOverlayVisible}
        voiceState={voiceState}
        isVoiceActive={isVoiceActive}
        onEndCall={handleEndVoice}
      />

    </div>
  );
};

const App = () => (
  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
    <HelmetProvider>
      <ThemeProvider>
        <PopupProvider>
          <AuthProvider>
            <VoiceProvider>
              <ChatInputProvider>
                <CartProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </CartProvider>
              </ChatInputProvider>
            </VoiceProvider>
          </AuthProvider>
        </PopupProvider>
      </ThemeProvider>
    </HelmetProvider>
  </GoogleOAuthProvider>
);

export default App;